import React, {useCallback, useMemo, useState} from "react";
import {AccountingTransactionTypes, CustomerPaymentTerms} from "../../../../../../../../../core/constants/enums";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import classnames from "classnames";
import {ReactComponent as RefundIcon} from "../../../../../../../../../assets/images/credit-card-refund.svg";
import {ReactComponent as Passed} from "../../../../../../../../../assets/images/validations/passed.svg";
import SummaryActionCard from "../../../../../../../../components/app-specific/summary-cards/summary-action-card";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import OrderBypassInfoCard from "../../../../../../../../components/app-specific/order-inforamtion-shared-sections/bypass-card";
import PayForOrderDialog from "../../../../../../../../components/dialogs/pay-for-order";
import {CircularProgress, Tooltip} from "@mui/material";
import {ReactComponent as Failed} from "../../../../../../../../../assets/images/validations/failed.svg";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {useParams} from "react-router-dom";
import {useAppSelector} from "../../../../../../../../redux";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {ReceiptLongRounded} from "@mui/icons-material";
import TransactionReceiptDialog from "../../../../../../../../components/dialogs/transaction-receipt-dialog";
import ToastService from "../../../../../../../../../core/services/toast";
import PartnerIndustryRules from "../../../../../../../../../core/services/partner-industry-rules";
import {usePartnerInformation} from "../../../../../../../../hooks/use-partner-information";
import CacheService from "../../../../../../../../../core/services/cache/cache_service";


const billInfoSummaryBodyIds = {
    subTotal: 'subTotal',
    discount: 'discount',
    shippingFee: 'shippingFee',
    tax: (i) => `tax-${i}`,
    total: 'total',
    transaction: (i) => `transaction-${i}`,
}

const billInfoSummaryActionIds = {
    nextStep: 'nextStep',
    pay: "pay",
    closed: "closed",
}

const initReceiptDialogState = {title: '', open: false, customerId: undefined, receiptId: undefined, close: () => void 0};

const SalesOrderInformationViewBillInfoSection = ({
                                                      order,
                                                      loading,
                                                      submittingDisabled,
                                                      generateNextOrderEntity: generateNextOrderEntityProps,
                                                      removeTransaction,
                                                      getOrderInformation,
                                                      payOrder,
                                                      customerId,
                                                      failedPreAuthPayments,
                                                      attemptedInvoiceGeneration,
                                                  }) => {
    const params = useParams();
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    const [openPayDialog, setOpenPayDialog] = useState(false);
    const [updatingTransactions, setUpdatingTransactions] = useState(false);
    const [generatingNextOrderEntity, setGeneratingNextOrderEntity] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels()
    const customer = useAppSelector(state => state.customerInformation);
    const partnerInformation = usePartnerInformation();

    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const partnerId = useMemo(() => partnerInformation?.id ?? CacheService.getOperatorPartnerId(), [partnerInformation?.id]);
    const forceCustomersAsCod = useMemo(() => PartnerIndustryRules.forcesCustomersAsCod(partnerId), [partnerId]);
    const isNeededToPay = useMemo(() => PartnerIndustryRules.effectiveIsNeededToPay(order, partnerId), [order, partnerId]);

    /**
     * Calls the [generateNextOrderEntity] callback provided by the props.
     *
     * * sets the loading state of the summary card for this action.
     * @type {(function(...[*]): Promise<void>)}
     */
    const generateNextOrderEntity = useCallback(async (...args) => {
        setGeneratingNextOrderEntity(true);
        await generateNextOrderEntityProps(...args);
        if (!isMounted())
            return;
        setGeneratingNextOrderEntity(false);
    }, [generateNextOrderEntityProps, isMounted])

    /**
     * Updates the transactions through the given mutator.
     * @type {(function(*=, ...[*]): Promise<void>)}
     */
    const updateTransaction = useCallback(async (mutator, ...args) => {
        setUpdatingTransactions(true);
        await mutator(...args);
        if (!isMounted())
            return;
        setUpdatingTransactions(false);
    }, [isMounted])

    /**
     * Gets users confirmation before removing the current order transaction entirely.
     * @param {any} transaction
     */
    const getRemoveTransactionConfirmation = useCallback((transaction) => {
        if (!Number.isFinite(Number(transaction?.id))) {
            ToastService.error({
                title: 'Refund failed',
                message: 'The transaction could not be refunded because its identifier is invalid.',
            });
            return;
        }
        ToastService.mainAPIRedZoneWarning();
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this transaction and
                    get {formatMoney(transaction?.amount ?? 0)} refunded to your account?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && updateTransaction(removeTransaction, transaction?.id),
        );
    }, [removeTransaction, updateTransaction])

    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: customerId,
            // TODO: receipt id for the print receipt
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [customerId])

    const billInfoSummaryBody = useMemo(() => {
        const res = [];
        const discountAmount = (order?.totalItemsDiscountAmount ?? 0);
        const shippingFeesAmount = (order?.freightFee ?? 0);
        const transactions = order?.appliedTransactions?.filter(e => ![AccountingTransactionTypes.userOrder, AccountingTransactionTypes.invoice].includes(e.transactionTypeName)) ?? [];
        res.push({
            id: billInfoSummaryBodyIds.subTotal,
            title: 'Sub total',
            value: order?.subTotal,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        })
        if (discountAmount > 0) {
            res.push({
                id: billInfoSummaryBodyIds.discount,
                value: -discountAmount,
                title: `Discount ${(order?.discountPercentage ?? 0) > 0 ? `(${order?.discountPercentage}%)` : ''}`,
                loadingValue: "loading...",
                format: formatMoney,
                action: {show: false},
            })
        }
        res.push({
            id: billInfoSummaryBodyIds.shippingFee,
            value: shippingFeesAmount,
            title: `Shipping Fee`,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        })
        let taxI = 0;
        for (const tax of order?.taxes ?? []) {
            res.push({
                id: billInfoSummaryBodyIds.tax(taxI++),
                title: <span className={classnames({'line-through': tax?.exemptNo?.length})}>
                    {tax.shortName ?? '--'} ({tax?.rate ?? '--'}%)
                </span>,
                value: tax?.exemptNo?.length ? `exempt: ${tax?.exemptNo}` : formatMoney(tax.total),
                loadingValue: "loading...",
                action: {show: false},
            })
        }
        res.push({
            id: billInfoSummaryBodyIds.total,
            title: 'Total',
            value: order?.total,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        });
        let transactionI = 0;
        for (const transaction of transactions) {
            switch (transaction?.transactionTypeName) {
                case AccountingTransactionTypes.giftCard:
                default:
                    res.push({
                        id: billInfoSummaryBodyIds.transaction(transaction?.id ?? --transactionI),
                        title: transaction?.transactionTypeDescription,
                        value: -(transaction?.amount ?? 0),
                        loadingValue: "loading...",
                        format: formatMoney,
                        action: {show: false},
                    })
                    break;
                case AccountingTransactionTypes.payment:
                case AccountingTransactionTypes.otherPayment: {
                    const showReceipt = transaction?.transactionTypeName === AccountingTransactionTypes.payment && transaction?.receiptId && operatorAccessLevels.packages.sales.paymentReceipts;
                    const showPrefix = attemptedInvoiceGeneration
                    const failed = failedPreAuthPayments?.some(e => e.id === transaction.id)
                    res.push({
                        id: billInfoSummaryBodyIds.transaction(transaction?.id ?? --transactionI),
                        transaction,
                        titlePrefix: !showPrefix
                            ? null
                            : !failed
                                ? <Passed className={'mr-2'}/>
                                : <Tooltip
                                    title={"This payment has been removed form the order and refunded because it was failed to be completed."}
                                    placement={'left'}
                                    arrow
                                >
                                    <Failed className={'mr-2'}/>
                                </Tooltip>,
                        title: transaction?.transactionTypeDescription,
                        titleSuffix:
                            showReceipt
                                ? <IconButton
                                    color={'paper.primary'}
                                    className={'ml-1'}
                                    onPressed={() => openReceiptDialog(transaction)}
                                >
                                    <ReceiptLongRounded className={'icon normal'}/>
                                </IconButton>
                                : null,
                        value: -(transaction?.amount ?? 0),
                        loadingValue: "loading...",
                        format: formatMoney,
                        action: {
                            show: !order?.isClosed && !failed && operatorAccessLevels.packages.sales.manageSalesOrders,
                            className: 'ml-2',
                            submitting: updatingTransactions,
                            icon: <RefundIcon className={'icon small'}/>,
                            submittingIcon: <CircularProgress color={'secondary'} size={15}/>,
                            props: {color: 'paper.error'},
                            onClick: (entry) => getRemoveTransactionConfirmation(entry.transaction),
                        },
                    })
                    break;
                }
            }
        }
        return res;
    }, [order?.totalItemsDiscountAmount, order?.freightFee, order?.appliedTransactions, order?.subTotal, order?.total, order?.discountPercentage, order?.taxes, order?.isClosed, operatorAccessLevels.packages.sales.paymentReceipts, operatorAccessLevels.packages.sales.manageSalesOrders, attemptedInvoiceGeneration, failedPreAuthPayments, updatingTransactions, openReceiptDialog, getRemoveTransactionConfirmation])


    const billInfoSummaryActions = useMemo(() => [
        {
            id: billInfoSummaryActionIds.closed,
            value: 'Order closed',
            submittingValue: 'Submitting...',
            show: !!order && !!order?.isClosed,
            disabled: true,
            variant: 'contained',
            onClick: () => false,
        },
        {
            id: billInfoSummaryActionIds.pay,
            value: isNeededToPay ? 'Pay this order' : "Add payment anyways",
            submittingValue: 'Submitting...',
            show: !!order && !order?.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders && (
                isNeededToPay
                || (!forceCustomersAsCod && order?.termsCode === CustomerPaymentTerms.COD && PartnerIndustryRules.hasOutstandingBalance(order))
                || (forceCustomersAsCod && !!order?.payInfo?.byPassed && PartnerIndustryRules.hasOutstandingBalance(order))
            ),
            disabled: loading || !order?.items?.length,
            variant: isNeededToPay ? 'contained' : "outlined",
            onClick: () => setOpenPayDialog(true),
        },
        {
            id: billInfoSummaryActionIds.nextStep,
            value: 'Generate Invoice',
            submittingValue: 'Generating...',
            show: !!order && !order?.isClosed && !isNeededToPay && operatorAccessLevels.packages.sales.manageSalesOrders,
            disabled:
                submittingDisabled
                || loading
                || generatingNextOrderEntity
                || !order?.items?.length
                || (
                    order?.items
                        ?.filter(e => e.partNo !== order?.appliedGiftCardInfo?.equalPartNo)
                        ?.some(e => parseFloat(e.committedQty) === 0 && parseFloat(e.backorderQty) === 0)
                    ?? true
                ),
            variant: 'contained',
            onClick: async () => await generateNextOrderEntity(),
        },
    ], [submittingDisabled, order, isNeededToPay, forceCustomersAsCod, operatorAccessLevels.packages.sales.manageSalesOrders, loading, generatingNextOrderEntity, generateNextOrderEntity])

    const billInfoSummary = useMemo(() => ({
        header: {
            title: 'Balance',
            value: order?.balance,
            loadingValue: "Loading...",
            format: formatMoney,
        },
        body: billInfoSummaryBody,
        actions: billInfoSummaryActions,
    }), [billInfoSummaryBody, billInfoSummaryActions, order?.balance])


    return (
        <>
            {
                operatorAccessLevels.packages.sales.quoteBypass &&
                <OrderBypassInfoCard
                    loading={loading}
                    orderId={orderId}
                    orderNo={order?.orderNo}
                    getOrderInformation={getOrderInformation}
                    customer={customer}
                />
            }
            <SummaryActionCard
                className={'auto-height'}
                header={billInfoSummary.header}
                body={billInfoSummary.body}
                actions={billInfoSummary.actions}
                loadingCount={billInfoSummary.body.length}
                loading={loading}
            />
            <PayForOrderDialog
                open={openPayDialog}
                close={() => setOpenPayDialog(false)}
                pay={payOrder}
                customerId={customerId}
                remainingBalance={PartnerIndustryRules.effectiveRemainingBalance(order, partnerId)}
            />
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
        </>
    )
}


export default SalesOrderInformationViewBillInfoSection;
